import { useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { act as ccAct } from "@/lib/ccApi";
import { useBoard } from "@/hooks/useBoard";
import {
  ROWS,
  GROUP_ROWS,
  TASK_ROWS,
  tasksOf,
  WORK_STATE,
  boardClock,
  isDoneGroup,
  ACTIVE_STATES,
} from "@/data/workBoard";
import { StartHere } from "@/screens/cc";
import { IdeaDock } from "@/screens/idea/IdeaDock";
import { GoalDetail } from "@/screens/goals/GoalDetail";
import { WorkGroup, WorkTaskRow } from "@/screens/shared/WorkAxis";
import { AddWorkBar } from "@/screens/cc/AddWorkBar";
import {
  Card,
  Label,
  MonoMeta,
  StateWord,
  SummaryStripBase,
  DetailOverlay,
  ItemEditorBar,
  NoteOnCard,
} from "@/design/primitives";

// ── Work (P3, 2026-10-21) ────────────────────────────────────────────────────
// One page, the goal-axis view: every chunk goal with the work filed under it,
// dated groups, and the needs-a-goal dock. The idea dock lives here too — the
// same capture → back-and-forth → convert loop as the Projects workshop, with
// ONE difference: converting lands the project on PROJECTS (the one home of
// project rows — Work never holds a phase-shaped row).
//
// Data: live from /api/board/work when reachable (the module is re-pushed by
// ingest-work.py), the committed derived module otherwise. Jeff's overlays
// (archive, notes, Kit claims) survive every re-push server-side.

// ── Task detail overlay (Work's own — the row's words, group, brand, editor) ──
const WorkTaskDetail = ({ row, groupTitle, variant, onClose, board }) => {
  const p = row.payload || {};
  return (
    <DetailOverlay
      variant={variant === "desktop" ? "panel" : "sheet"}
      onClose={onClose}
      label={`${p.title || row.slug} details`}
      testId="work-task-detail"
    >
      <div className="flex items-baseline justify-between gap-3 pr-12 pt-2">
        <h2 className="min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-os-ink">
          {p.title || row.slug}
        </h2>
        <StateWord tone={(WORK_STATE[row.state] || WORK_STATE.UNSORTED).tone}>
          {(WORK_STATE[row.state] || WORK_STATE.UNSORTED).word}
        </StateWord>
      </div>
      <MonoMeta as="p" className="mt-1">
        {p.brand || ""}
      </MonoMeta>
      <div className="mt-6 border-t border-os-border">
        {groupTitle && (
          <section className="border-b border-os-border py-4">
            <Label as="h3">Filed under</Label>
            <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">
              {groupTitle}
            </p>
          </section>
        )}
        {p.why && (
          <section className="border-b border-os-border py-4">
            <Label as="h3">Why it's on the board</Label>
            <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">
              {p.why}
            </p>
          </section>
        )}
        {p.next && (
          <section className="border-b border-os-border py-4">
            <Label as="h3">Next</Label>
            <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">
              {p.next}
            </p>
          </section>
        )}
        {p.blockersLine && (
          <section className="border-b border-os-border py-4">
            <Label as="h3">Standing</Label>
            <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">
              {p.blockersLine}
            </p>
          </section>
        )}
      </div>
      {p.jeff?.kitTaken && (
        <p
          className="mt-3 text-[0.8125rem] font-medium text-os-green"
          data-testid="work-detail-taken"
        >
          Kit is on it
          {p.jeff.kitTaken.at
            ? ` — since ${new Date(p.jeff.kitTaken.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
            : ""}
          .
        </p>
      )}
      {board?.live && (
        <>
          <ItemEditorBar
            board={board}
            item={board.items.find((r) => r.slug === row.slug)}
            onClose={onClose}
            testId={`work-editor-${row.slug}`}
          />
          <NoteOnCard
            board={board}
            slug={row.slug}
            testId={`work-note-${row.slug}`}
          />
        </>
      )}
    </DetailOverlay>
  );
};

// ── The page ──
export default function WorkBoardPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const clock = useMemo(() => boardClock(), []);
  const board = useBoard("work");
  const [openSlug, setOpenSlug] = useState(null);
  const [flash, setFlash] = useState(null);
  const [showDone, setShowDone] = useState(false);

  const liveItems = board.items;
  const rows = board.live ? liveItems : ROWS;
  const groups = rows.filter((r) => r.kind === "group");
  const tasks = rows.filter((r) => r.kind === "task");

  // Jeff's overlay reading on live: ARCHIVED tasks fold away; the module itself
  // carries no archived tasks (the derivation excludes them at the source).
  const visibleTasks = tasks.filter((t) => t.state !== "ARCHIVED");
  const doneTasks = tasks.filter((t) => t.state === "ARCHIVED");

  const activeGroups = groups.filter((g) => ACTIVE_STATES.includes(g.state));
  const datedGroups = groups.filter(
    (g) => g.state === "UNSORTED" && g.slug !== "needs-a-goal",
  );
  const dock = groups.find((g) => g.slug === "needs-a-goal");
  const doneGroups = groups.filter(isDoneGroup);

  const summary = useMemo(() => {
    const needsGoal = dock ? tasksOf("needs-a-goal").length : 0;
    const running = visibleTasks.filter((t) => t.state === "EXECUTE").length;
    const parked = visibleTasks.filter((t) => t.state === "TABLED").length;
    const parts = [];
    if (needsGoal)
      parts.push({
        key: "dock",
        text: `${needsGoal} need a goal`,
        tone: "amber",
      });
    if (running)
      parts.push({
        key: "running",
        text: `${running} being worked`,
        tone: "neutral",
      });
    if (parked)
      parts.push({ key: "parked", text: `${parked} parked`, tone: "neutral" });
    if (!parts.length)
      parts.push({
        key: "quiet",
        text: "everything is filed under a goal",
        tone: "green",
      });
    return {
      parts,
      tone: needsGoal ? "amber" : "neutral",
      text: parts.map((x) => x.text).join(" · "),
    };
  }, [dock, visibleTasks]);

  const startHere = !board.live
    ? {
        tone: "muted",
        text: "Work shows the latest committed derivation — actions come back when the desk is reachable",
      }
    : (dock ? tasksOf("needs-a-goal").length : 0) > 0
      ? {
          tone: "amber",
          text: `${tasksOf("needs-a-goal").length} pieces of work have no goal yet — file them or let them ride below` /* no button — filing is a thinking job */,
        }
      : {
          tone: "green",
          text: "Every task sits under a goal — nothing orphaned.",
        };

  const openRow = rows.find((r) => r.slug === openSlug) || null;
  const openGroupTitle =
    openRow && openRow.kind === "task"
      ? groups.find((g) => g.slug === openRow.payload?.goalRef)?.payload
          ?.title || ""
      : "";

  // Chunks read in the GoalCard overlay (they ARE goals-module cards); tasks
  // read in Work's own detail. handleOpen routes by the row's kind.
  const handleOpen = (row) => setOpenSlug(row.slug);

  return (
    <div data-testid="work-page" className="w-full font-inter text-os-ink">
      <div
        className={
          desktop
            ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6"
            : "flex flex-col gap-3 px-4 pb-8 pt-4"
        }
      >
        <SummaryStripBase
          {...summary}
          size={desktop ? "lg" : "sm"}
          meta={
            clock.fresh ? (
              <MonoMeta
                as="p"
                data-testid="work-as-of"
                className="shrink-0 whitespace-nowrap pt-1 text-os-ink"
              >
                as of {clock.stampText}
              </MonoMeta>
            ) : (
              <Card
                as="section"
                data-testid="work-stale-banner"
                data-dead={clock.dead}
                aria-label="Board freshness warning"
                className="px-5 py-3"
              >
                <p
                  className={cn(
                    "text-[0.875rem] leading-[1.45]",
                    clock.dead ? "text-os-red" : "text-os-amber",
                  )}
                >
                  {clock.dead
                    ? "Work is not checking right now — Kit needs to look at the updater"
                    : `Work info is from ${clock.stampText} — Kit's passes keep it current`}
                </p>
              </Card>
            )
          }
        />
        <StartHere {...startHere} testId="work-start-here" />
        {flash && (
          <p
            data-testid="work-flash"
            className="text-[0.875rem] font-medium text-os-green"
          >
            {flash}
          </p>
        )}

        {activeGroups.map((g) => (
          <WorkGroup
            key={g.slug}
            group={g}
            tasks={visibleTasks.filter((t) => t.payload?.goalRef === g.slug)}
            onOpen={handleOpen}
          />
        ))}

        {datedGroups.length > 0 && (
          <section className="flex flex-col gap-4" data-testid="work-dated">
            <Label as="h2">Work with a date attached</Label>
            {datedGroups.map((g) => (
              <WorkGroup
                key={g.slug}
                group={g}
                tasks={visibleTasks.filter(
                  (t) => t.payload?.goalRef === g.slug,
                )}
                onOpen={handleOpen}
              />
            ))}
          </section>
        )}

        {dock &&
          visibleTasks.some((t) => t.payload?.goalRef === "needs-a-goal") && (
            <WorkGroup
              group={dock}
              tasks={visibleTasks.filter(
                (t) => t.payload?.goalRef === "needs-a-goal",
              )}
              onOpen={handleOpen}
            />
          )}

        {doneGroups.length > 0 && (
          <section
            className="flex flex-col gap-2"
            data-testid="work-done-groups"
          >
            <button
              type="button"
              data-testid="work-toggle-done"
              aria-expanded={showDone}
              onClick={() => setShowDone((s) => !s)}
              className="self-start font-mono text-[12px] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline"
            >
              {showDone
                ? "Hide finished goals"
                : `Show finished goals (${doneGroups.length})`}
            </button>
            {showDone &&
              doneGroups.map((g) => (
                <WorkGroup
                  key={g.slug}
                  group={g}
                  tasks={visibleTasks.filter(
                    (t) => t.payload?.goalRef === g.slug,
                  )}
                  onOpen={handleOpen}
                  defaultOpen={false}
                />
              ))}
          </section>
        )}

        <IdeaDock onFlash={setFlash} desktop={desktop} />
      </div>

      {openRow && openRow.kind === "task" && (
        <WorkTaskDetail
          row={openRow}
          groupTitle={openGroupTitle}
          variant={desktop ? "desktop" : "mobile"}
          onClose={() => setOpenSlug(null)}
          board={board}
        />
      )}
      {openRow && openRow.kind === "group" && (
        <GoalDetail
          card={openRow.payload}
          variant={desktop ? "desktop" : "mobile"}
          onClose={() => setOpenSlug(null)}
          editor={board.live ? { board, item: openRow } : null}
        />
      )}
    </div>
  );
}
